import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';

const DataRetentionSettings = ({ retentionSettings, onRetentionSettingsChange }) => {
  const [showDeletionModal, setShowDeletionModal] = useState(false);
  const [selectedDataType, setSelectedDataType] = useState(null);

  const retentionPeriods = [
    { value: 'never', label: 'Never (Manual only)' },
    { value: '30', label: '30 days' },
    { value: '90', label: '90 days' },
    { value: '180', label: '6 months' },
    { value: '365', label: '1 year' },
    { value: '1095', label: '3 years' },
    { value: '1825', label: '5 years' }
  ];

  const dataTypes = [
    {
      id: 'credentials',
      name: 'Verifiable Credentials',
      description: 'Digital certificates, diplomas, licenses, and other issued credentials',
      currentRetention: retentionSettings?.credentials?.period || 'never',
      autoDelete: retentionSettings?.credentials?.autoDelete || false,
      itemCount: 47,
      lastCleaned: '2024-08-15T10:30:00Z'
    },
    {
      id: 'verificationHistory',
      name: 'Verification History',
      description: 'Records of credential verification requests and responses',
      currentRetention: retentionSettings?.verificationHistory?.period || '365',
      autoDelete: retentionSettings?.verificationHistory?.autoDelete || true,
      itemCount: 1234,
      lastCleaned: '2024-09-01T02:00:00Z'
    },
    {
      id: 'proofGeneration',
      name: 'Proof Generation Logs',
      description: 'Technical logs from zero-knowledge proof generation processes',
      currentRetention: retentionSettings?.proofGeneration?.period || '90',
      autoDelete: retentionSettings?.proofGeneration?.autoDelete || true,
      itemCount: 892,
      lastCleaned: '2024-09-01T02:00:00Z'
    },
    {
      id: 'keyMaterial',
      name: 'Cryptographic Key History',
      description: 'Historical records of rotated and retired cryptographic keys',
      currentRetention: retentionSettings?.keyMaterial?.period || '1825',
      autoDelete: retentionSettings?.keyMaterial?.autoDelete || false,
      itemCount: 12,
      lastCleaned: 'Never'
    },
    {
      id: 'consentRecords',
      name: 'Consent Records',
      description: 'Data sharing agreements and consent management history',
      currentRetention: retentionSettings?.consentRecords?.period || '1095',
      autoDelete: retentionSettings?.consentRecords?.autoDelete || true,
      itemCount: 156,
      lastCleaned: '2024-08-20T02:00:00Z'
    },
    {
      id: 'auditLogs',
      name: 'Security Audit Logs',
      description: 'System access logs, security events, and audit trail records',
      currentRetention: retentionSettings?.auditLogs?.period || '365',
      autoDelete: retentionSettings?.auditLogs?.autoDelete || true,
      itemCount: 5678,
      lastCleaned: '2024-09-01T02:00:00Z'
    }
  ];

  const handleRetentionChange = (dataTypeId, setting, value) => {
    const updatedSettings = {
      ...retentionSettings,
      [dataTypeId]: {
        ...retentionSettings?.[dataTypeId],
        [setting]: value
      }
    };
    onRetentionSettingsChange(updatedSettings);
  };

  const handleManualDeletion = (dataTypeId) => {
    setSelectedDataType(dataTypeId);
    setShowDeletionModal(true);
  };

  const confirmDeletion = () => {
    // Handle manual deletion logic here
    setShowDeletionModal(false);
    setSelectedDataType(null);
  };

  const getRetentionLabel = (period) => {
    const option = retentionPeriods?.find(p => p?.value === period);
    return option ? option?.label : 'Custom';
  };

  const getRetentionColor = (period) => {
    switch (period) {
      case 'never': return 'text-warning';
      case '30': case'90': return 'text-success';
      case '180': case'365': return 'text-primary';
      default: return 'text-muted-foreground';
    }
  };

  const formatDate = (dateString) => {
    if (dateString === 'Never') return 'Never';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStorageImpact = (itemCount, period) => {
    if (period === 'never') return 'High';
    if (parseInt(period) <= 90) return 'Low';
    if (parseInt(period) <= 365) return 'Medium';
    return 'High';
  };

  const getStorageImpactColor = (impact) => {
    switch (impact) {
      case 'Low': return 'text-success';
      case 'Medium': return 'text-warning';
      case 'High': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Data Retention Settings</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Configure automatic credential expiration and data deletion policies
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="Clock" size={20} className="text-primary" />
          <span className="text-sm font-medium text-primary">Automated Cleanup</span>
        </div>
      </div>
      <div className="space-y-6">
        {dataTypes?.map((dataType) => {
          const storageImpact = getStorageImpact(dataType?.itemCount, dataType?.currentRetention);
          
          return (
            <div key={dataType?.id} className="border border-border rounded-lg p-4">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h4 className="font-medium text-foreground">{dataType?.name}</h4>
                  <p className="text-sm text-muted-foreground mt-1">{dataType?.description}</p>
                  
                  <div className="flex items-center space-x-4 mt-3 text-sm">
                    <div>
                      <span className="text-muted-foreground">Items:</span>
                      <span className="ml-1 text-foreground font-medium">
                        {dataType?.itemCount?.toLocaleString()}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Last Cleaned:</span>
                      <span className="ml-1 text-foreground">{formatDate(dataType?.lastCleaned)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Storage Impact:</span>
                      <span className={`ml-1 font-medium ${getStorageImpactColor(storageImpact)}`}>
                        {storageImpact}
                      </span>
                    </div>
                  </div>
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Trash2"
                  iconPosition="left"
                  onClick={() => handleManualDeletion(dataType?.id)}
                >
                  Clean Now
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <label className="text-sm font-medium text-foreground">Retention Period</label>
                  <Select
                    options={retentionPeriods}
                    value={dataType?.currentRetention}
                    onChange={(value) => handleRetentionChange(dataType?.id, 'period', value)}
                    placeholder="Select retention period"
                  />
                  <div className="flex items-center space-x-2 text-sm">
                    <Icon name="Clock" size={14} className={getRetentionColor(dataType?.currentRetention)} />
                    <span className={getRetentionColor(dataType?.currentRetention)}>
                      {getRetentionLabel(dataType?.currentRetention)}
                    </span>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <label className="text-sm font-medium text-foreground">Automation Settings</label>
                  <div className="space-y-2">
                    <Checkbox
                      label="Enable automatic deletion"
                      description="Automatically delete data when retention period expires"
                      checked={dataType?.autoDelete}
                      onChange={(e) => handleRetentionChange(dataType?.id, 'autoDelete', e?.target?.checked)}
                    />
                    
                    {dataType?.id === 'credentials' && (
                      <Checkbox
                        label="Notify before deletion"
                        description="Send notification 7 days before credential expiration"
                        checked={retentionSettings?.credentials?.notifyBeforeDeletion || false}
                        onChange={(e) => handleRetentionChange(dataType?.id, 'notifyBeforeDeletion', e?.target?.checked)}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {/* Global Retention Settings */}
      <div className="mt-6 p-4 bg-muted/30 rounded-lg">
        <h4 className="font-medium text-foreground mb-4">Global Retention Policies</h4>
        <div className="space-y-3">
          <Checkbox
            label="Enable privacy-first deletion"
            description="Prioritize data minimization and delete data as soon as legally permissible"
            checked={retentionSettings?.global?.privacyFirst || false}
            onChange={(e) => handleRetentionChange('global', 'privacyFirst', e?.target?.checked)}
          />
          
          <Checkbox
            label="Secure deletion"
            description="Use cryptographic erasure and secure deletion methods for sensitive data"
            checked={retentionSettings?.global?.secureDelete || true}
            onChange={(e) => handleRetentionChange('global', 'secureDelete', e?.target?.checked)}
          />
          
          <Checkbox
            label="Compliance mode"
            description="Automatically adjust retention periods based on regulatory requirements"
            checked={retentionSettings?.global?.complianceMode || false}
            onChange={(e) => handleRetentionChange('global', 'complianceMode', e?.target?.checked)}
          />
        </div>
      </div>
      {/* Manual Deletion Modal */}
      {showDeletionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <Icon name="Trash2" size={24} className="text-error" />
              <h3 className="text-lg font-semibold text-foreground">Confirm Data Deletion</h3>
            </div>
            
            <p className="text-sm text-muted-foreground mb-6">
              Are you sure you want to permanently delete all expired data of this type? 
              This action cannot be undone and will remove all data that has exceeded its retention period.
            </p>
            
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowDeletionModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={confirmDeletion}
                className="flex-1"
              >
                Delete Data
              </Button>
            </div>
          </div>
        </div>
      )}
      <div className="mt-6 p-4 bg-muted/30 rounded-lg">
        <div className="flex items-start space-x-3">
          <Icon name="Shield" size={16} className="text-primary mt-0.5" />
          <div className="text-sm">
            <p className="text-foreground font-medium">Data Minimization</p>
            <p className="text-muted-foreground mt-1">
              Automatic data retention policies help minimize your digital footprint while maintaining 
              necessary records for security and compliance purposes. All deletions use secure erasure methods.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataRetentionSettings;